import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Header } from "@/components/Layout/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { HelpTooltip } from "@/components/ui/help-tooltip";
import {
  useOrganizationSettings,
  useUpdateOrganizationSettings,
} from "@/hooks/useOrganizationSettings";
import { useToast } from "@/hooks/useToast";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { useEffect } from "react";

const settingsSchema = z.object({
  organization_name: z.string().min(1, "Nom requis"),
  legal_form: z.string().optional(),
  siret: z.string().optional(),
  address: z.string().optional(),
  postal_code: z.string().optional(),
  city: z.string().optional(),
  country: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email("Email invalide").optional().or(z.literal("")),
  website: z.string().optional(),
  vat_number: z.string().optional(),
  vat_exempt: z.boolean(),
  vat_exempt_reason: z.string().optional(),
  default_validity_days: z.coerce.number().min(1).max(365),
  default_deposit_percentage: z.coerce.number().min(0).max(100),
  bank_name: z.string().optional(),
  bank_iban: z.string().optional(),
  bank_bic: z.string().optional(),
});

type SettingsFormData = z.infer<typeof settingsSchema>;

export function SettingsPage() {
  const { isAdmin } = useAuth();
  const { data: settings, isLoading } = useOrganizationSettings();
  const updateSettings = useUpdateOrganizationSettings();
  const { toast } = useToast();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<SettingsFormData>({
    resolver: zodResolver(settingsSchema),
    defaultValues: {
      organization_name: "Communauté des Béatitudes",
      legal_form: "Association Loi 1901",
      vat_exempt: true,
      vat_exempt_reason:
        "TVA non applicable - Article 293 B du Code Général des Impôts",
      default_validity_days: 30,
      default_deposit_percentage: 30,
      country: "France",
    },
  });

  useEffect(() => {
    if (settings) {
      reset({
        organization_name: settings.organization_name,
        legal_form: settings.legal_form || "",
        siret: settings.siret || "",
        address: settings.address || "",
        postal_code: settings.postal_code || "",
        city: settings.city || "",
        country: settings.country || "France",
        phone: settings.phone || "",
        email: settings.email || "",
        website: settings.website || "",
        vat_number: settings.vat_number || "",
        vat_exempt: settings.vat_exempt,
        vat_exempt_reason: settings.vat_exempt_reason || "",
        default_validity_days: settings.default_validity_days,
        default_deposit_percentage: settings.default_deposit_percentage,
        bank_name: settings.bank_name || "",
        bank_iban: settings.bank_iban || "",
        bank_bic: settings.bank_bic || "",
      });
    }
  }, [settings, reset]);

  const vatExempt = watch("vat_exempt");

  const onSubmit = async (data: SettingsFormData) => {
    try {
      await updateSettings.mutateAsync({
        organization_name: data.organization_name,
        legal_form: data.legal_form || null,
        siret: data.siret || null,
        address: data.address || null,
        postal_code: data.postal_code || null,
        city: data.city || null,
        country: data.country || null,
        phone: data.phone || null,
        email: data.email || null,
        website: data.website || null,
        vat_number: data.vat_number || null,
        vat_exempt: data.vat_exempt,
        vat_exempt_reason: data.vat_exempt_reason || null,
        default_validity_days: data.default_validity_days,
        default_deposit_percentage: data.default_deposit_percentage,
        bank_name: data.bank_name || null,
        bank_iban: data.bank_iban || null,
        bank_bic: data.bank_bic || null,
      });
      toast({ title: "Paramètres enregistrés" });
    } catch {
      toast({
        title: "Erreur",
        description: "Impossible d'enregistrer les paramètres",
        variant: "destructive",
      });
    }
  };

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <Header title="Paramètres" />

      <div className="flex-1 overflow-auto p-4 md:p-6">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="mx-auto max-w-3xl space-y-6"
        >
          <Card>
            <CardHeader>
              <CardTitle>Informations légales</CardTitle>
              <CardDescription>
                Ces informations apparaissent sur les devis et documents
                officiels
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="organization_name">
                    Nom de l'organisation *
                  </Label>
                  <Input
                    id="organization_name"
                    {...register("organization_name")}
                  />
                  {errors.organization_name && (
                    <p className="text-sm text-destructive">
                      {errors.organization_name.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="legal_form">Forme juridique</Label>
                  <Input
                    id="legal_form"
                    placeholder="Association Loi 1901"
                    {...register("legal_form")}
                  />
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <div className="flex items-center gap-1">
                    <Label htmlFor="siret">SIRET</Label>
                    <HelpTooltip content="Numéro SIRET à 14 chiffres, obligatoire pour les devis" />
                  </div>
                  <Input
                    id="siret"
                    placeholder="XXX XXX XXX XXXXX"
                    {...register("siret")}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="vat_number">N° TVA intracommunautaire</Label>
                  <Input
                    id="vat_number"
                    placeholder="FR XX XXX XXX XXX"
                    {...register("vat_number")}
                  />
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <Label htmlFor="address">Adresse</Label>
                <Textarea
                  id="address"
                  rows={2}
                  placeholder="Numéro et nom de rue"
                  {...register("address")}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="postal_code">Code postal</Label>
                  <Input id="postal_code" {...register("postal_code")} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="city">Ville</Label>
                  <Input id="city" {...register("city")} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="country">Pays</Label>
                  <Input id="country" {...register("country")} />
                </div>
              </div>

              <Separator />

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="phone">Téléphone</Label>
                  <Input id="phone" type="tel" {...register("phone")} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" {...register("email")} />
                  {errors.email && (
                    <p className="text-sm text-destructive">
                      {errors.email.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="website">Site web</Label>
                <Input
                  id="website"
                  placeholder="https://..."
                  {...register("website")}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>TVA</CardTitle>
              <CardDescription>
                Configuration de la TVA pour les devis
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="vat_exempt">Exonération de TVA</Label>
                  <p className="text-sm text-muted-foreground">
                    Activer si l'organisation n'est pas assujettie à la TVA
                  </p>
                </div>
                <Switch
                  id="vat_exempt"
                  checked={vatExempt}
                  onCheckedChange={(checked: boolean) =>
                    setValue("vat_exempt", checked, { shouldDirty: true })
                  }
                />
              </div>

              {vatExempt && (
                <div className="space-y-2">
                  <div className="flex items-center gap-1">
                    <Label htmlFor="vat_exempt_reason">
                      Mention légale d'exonération
                    </Label>
                    <HelpTooltip content="Mention obligatoire sur les devis pour justifier l'absence de TVA" />
                  </div>
                  <Textarea
                    id="vat_exempt_reason"
                    placeholder="TVA non applicable - Article 293 B du Code Général des Impôts"
                    {...register("vat_exempt_reason")}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Paramètres des devis</CardTitle>
              <CardDescription>
                Valeurs par défaut pour la création de devis
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <div className="flex items-center gap-1">
                    <Label htmlFor="default_validity_days">
                      Durée de validité (jours)
                    </Label>
                    <HelpTooltip content="Nombre de jours pendant lesquels le devis reste valable après émission" />
                  </div>
                  <Input
                    id="default_validity_days"
                    type="number"
                    min={1}
                    max={365}
                    {...register("default_validity_days")}
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-1">
                    <Label htmlFor="default_deposit_percentage">
                      Acompte par défaut (%)
                    </Label>
                    <HelpTooltip content="Pourcentage du montant total demandé à la signature du devis" />
                  </div>
                  <Input
                    id="default_deposit_percentage"
                    type="number"
                    min={0}
                    max={100}
                    {...register("default_deposit_percentage")}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Coordonnées bancaires</CardTitle>
              <CardDescription>
                Informations pour le paiement (optionnel)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="bank_name">Nom de la banque</Label>
                <Input id="bank_name" {...register("bank_name")} />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="bank_iban">IBAN</Label>
                  <Input
                    id="bank_iban"
                    placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                    {...register("bank_iban")}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bank_bic">BIC</Label>
                  <Input id="bank_bic" {...register("bank_bic")} />
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => reset()}
              disabled={!isDirty}
            >
              Annuler
            </Button>
            <Button type="submit" disabled={isSubmitting || !isDirty}>
              {isSubmitting ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
